// Drate's own plans: what a creator space pays Drate for. Separate from the memberships a
// creator sells to members. Free, Regular and Pro are fixed here. Custom plans are made from
// /admin/plans, carry Pro's features, and are never offered on the pricing page. Regular is
// offered behind the plans.regular flag, which hides Free from the people it names.

import { fileSize } from "./file-size";

/** Which features a plan carries: Free's or Pro's. Regular is paid and carries Free's. */
export type PlanTier = "free" | "pro";
export type PlanInterval = "month" | "year";
export const signupTrialDays = 14;
export type PlanKind = "free" | "regular" | "pro" | "custom";
/** How a space came to be on its plan. */
export type PlanSource = "free" | "subscription" | "trial" | "complimentary" | "account";

export type PlanPrice = { amount: number; interval: PlanInterval };

/** Drate's fee on a creator's sales, by plan. Stripe takes it as an application fee. */
export const platformFeePercent: Record<PlanTier, number> = { free: 5, pro: 5 };

/**
 * Drate's fee on a tip (shared/src/tips.ts), on every plan: under the 5% Buy Me a Coffee takes,
 * and the same whatever the space pays, so the free Tips plan never undercuts Regular or Pro.
 */
export const tipFeePercent = 4;

/**
 * Drate's fee on one sale: `percent` of an amount in the currency's smallest unit (cents, or
 * whole yen in a zero-decimal currency), rounded to the nearest unit. The percent counts to
 * the hundredth, as Stripe takes it, which keeps every halfway case exact: 10¢ at 5% is 1¢.
 */
export function platformFeeMinor(amountMinor: number, percent: number) {
  const basisPoints = Math.round(percent * 100),
    scaled = amountMinor * basisPoints;
  if (
    !Number.isSafeInteger(amountMinor) ||
    amountMinor < 0 ||
    !(basisPoints >= 0 && basisPoints <= 10_000) ||
    !Number.isSafeInteger(scaled)
  )
    throw new RangeError("A platform fee needs a whole amount and a percent from 0 to 100.");
  return Math.round(scaled / 10_000);
}

/**
 * File storage a space has on each tier, in decimal bytes as file browsers count them. It covers
 * every file the space keeps on Drate: videos, audio, images, documents, downloads and artwork.
 */
export const storageLimitBytes: Record<PlanTier, number> = { free: 10e9, pro: 100e9 };

/** Stripe identity for Pro, shared with the homepage Payment Links. */
export const proProductId = "drate7_creator_pro";
export const proLookupKey = (interval: PlanInterval) =>
  `drate7_creator_pro_${interval}_usd_${interval === "year" ? "v3" : "v1"}`;
export const proPrices: Record<PlanInterval, PlanPrice> = {
  month: { amount: 1200, interval: "month" },
  year: { amount: 9900, interval: "year" },
};

/** Stripe identity for Regular. Its prices are made on first use, in test and live mode. */
export const regularProductId = "drate7_creator_regular";
export const regularLookupKey = (interval: PlanInterval) =>
  `drate7_creator_regular_${interval}_usd_v1`;
export const regularPrices: Record<PlanInterval, PlanPrice> = {
  month: { amount: 900, interval: "month" },
  year: { amount: 6000, interval: "year" },
};

/** The plans anyone can buy outright, and what each costs. */
export const paidPlanPrices = { regular: regularPrices, pro: proPrices } as const;
export type PaidPlan = keyof typeof paidPlanPrices;
export const isPaidPlan = (plan: string): plan is PaidPlan => plan === "regular" || plan === "pro";

/** What paying yearly saves on a plan, in cents. */
export const yearlySaving = (plan: PaidPlan) =>
  paidPlanPrices[plan].month.amount * 12 - paidPlanPrices[plan].year.amount;

/** Keep older annual subscriptions recognizable after the price changes for new buyers. */
export function proIntervalForLookupKey(key: unknown): PlanInterval | null {
  if (key === proLookupKey("month")) return "month";
  if (
    key === proLookupKey("year") ||
    key === "drate7_creator_pro_year_usd_v2" ||
    key === "drate7_creator_pro_year_usd_v1"
  )
    return "year";
  return null;
}

/** Stripe identity for a custom plan, so making it twice finds the first. */
export const customProductId = (slug: string) => `drate7_plan_${slug}`;
export const customLookupKey = (slug: string, interval: PlanInterval) =>
  `drate7_plan_${slug}_${interval}_usd_v1`;

export const planSlugPattern = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
export const reservedPlanSlugs = new Set(["free", "regular", "pro", "custom", "trial"]);

/** Shared feature copy and availability for Settings › Plan and marketing comparisons.
 * Custom plans have Pro's column; Regular has Free's features at a price. Keep individual
 * capabilities visible on both surfaces. */
export const planComparison = [
  {
    group: "Your home",
    key: "website",
    feature: "Your own website, domain and look",
    free: true,
    regular: true,
    pro: true,
  },
  {
    group: "Your home",
    key: "app",
    feature: "Your own iPhone and Android app",
    free: false,
    regular: false,
    pro: true,
  },
  {
    group: "Your home",
    key: "flows",
    feature: "Custom flows, hand built for you",
    free: false,
    regular: false,
    pro: true,
  },
  {
    group: "Memberships & content",
    key: "tiers",
    feature: "Monthly & yearly memberships",
    free: true,
    regular: true,
    pro: true,
  },
  {
    group: "Memberships & content",
    key: "passes",
    feature: "Fixed-length passes",
    free: true,
    regular: true,
    pro: true,
  },
  {
    group: "Memberships & content",
    key: "posts",
    feature: "Members-only posts, video & downloads",
    free: true,
    regular: true,
    pro: true,
  },
  {
    group: "Memberships & content",
    key: "library",
    feature: "Courses & content library",
    free: true,
    regular: true,
    pro: true,
  },
  {
    group: "Memberships & content",
    key: "community",
    feature: "Members community feed",
    free: true,
    regular: true,
    pro: true,
  },
  {
    group: "Memberships & content",
    key: "bookings",
    feature: "Paid coaching & bookings",
    free: true,
    regular: true,
    pro: true,
  },
  {
    group: "Memberships & content",
    key: "discord",
    feature: "Discord roles by membership",
    free: true,
    regular: true,
    pro: true,
  },
  {
    group: "Memberships & content",
    key: "patreon",
    feature: "Keep Patreon connected",
    free: true,
    regular: true,
    pro: true,
  },
  { group: "Merch", key: "merch", feature: "Merch store", free: true, regular: true, pro: true },
  {
    group: "Merch",
    key: "pod",
    feature: "Print on demand, no inventory",
    free: true,
    regular: true,
    pro: true,
  },
  {
    group: "Merch",
    key: "fourthwall",
    feature: "Bring your Fourthwall shop",
    free: true,
    regular: true,
    pro: true,
  },
  {
    group: "Plan & support",
    key: "fee",
    feature: "Fee on membership sales",
    free: `${platformFeePercent.free}%`,
    regular: `${platformFeePercent.free}%`,
    pro: `${platformFeePercent.pro}%`,
  },
  {
    group: "Plan & support",
    key: "storage",
    feature: "File storage",
    free: fileSize(storageLimitBytes.free),
    regular: fileSize(storageLimitBytes.free),
    pro: fileSize(storageLimitBytes.pro),
  },
  {
    group: "Plan & support",
    key: "payouts",
    feature: "Who pays you",
    free: "Your Stripe, directly",
    regular: "Your Stripe, directly",
    pro: "Your Stripe, directly",
  },
  {
    group: "Plan & support",
    key: "team",
    feature: "Invite teammates to your dashboard",
    free: false,
    regular: false,
    pro: true,
  },
  {
    group: "Plan & support",
    key: "support",
    feature: "We handle your customer support",
    free: false,
    regular: false,
    pro: true,
  },
] as const satisfies readonly {
  group: string;
  key: string;
  feature: string;
  free: string | boolean;
  regular: string | boolean;
  pro: string | boolean;
}[];

export type PlanFeatureKey = (typeof planComparison)[number]["key"];

export const planComparisonGroups = [...new Set(planComparison.map((row) => row.group))];

/** "$12" or "$479.99" for an amount in cents. */
export function formatPlanAmount(cents: number) {
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

/** "$480 a year" or "$12 a month". */
export function formatPlanPrice(price: PlanPrice) {
  return `${formatPlanAmount(price.amount)} a ${price.interval}`;
}

/** A slug for a custom plan's name: "Migz Academy" → "migz-academy". */
export function planSlug(name: string) {
  return (
    name
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40)
      .replace(/-+$/, "") || "plan"
  );
}
